import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import config from '../../config';
import { useAppContext } from '../AppContext';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';

const formatDateTime = (isoString) => {
  if (!isoString) return 'Mai';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString('ca-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return isoString;
  }
};

const isProtectedAdmin = (user) => {
  return Boolean(user?.email && user.email.trim().toLowerCase() === 'cafarre@gmail.com');
};

const extractErrorMessage = (err, fallback) => {
  if (err.response?.status === 403) {
    return "Accés denegat (403): Cal el rol d’administrador (ROLE_ADMIN). Si acabes d’actualitzar el backend, fes Logout i torna a iniciar sessió amb Google per renovar el teu token.";
  }
  return (
    err.response?.data?.detail ||
    err.response?.data?.message ||
    (typeof err.response?.data === 'string' ? err.response.data : null) ||
    fallback
  );
};

const UsuarisAdmin = ({ showNotification, onReloadAll }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { ctxUser } = useAppContext();
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal Crear / Editar Usuari
  const [openUserModal, setOpenUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    usuari: '',
    email: '',
    actiu: true
  });
  const [savingUser, setSavingUser] = useState(false);

  // Confirmació Desactivar Usuari
  const [openToggleDialog, setOpenToggleDialog] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggling, setToggling] = useState(false);

  // Modal de Gestió de Comptes de l'Usuari
  const [openComptesModal, setOpenComptesModal] = useState(false);
  const [selectedUserForComptes, setSelectedUserForComptes] = useState(null);
  const [newCompteApp, setNewCompteApp] = useState('');
  const [newCompteOrdre, setNewCompteOrdre] = useState(1);
  const [savingCompte, setSavingCompte] = useState(false);

  // Carregar usuaris
  const fetchUsers = useCallback(async () => {
    if (!jwtToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/v1/usuari/list`, {
        headers: { Authorization: jwtToken }
      });
      const data = Array.isArray(res.data) ? res.data : [];
      const sorted = [...data].sort((a, b) => a.usuari.localeCompare(b.usuari));
      setUsers(sorted);

      // Si tenim el modal de comptes obert d'un usuari, actualitzar-lo
      setSelectedUserForComptes((prev) => {
        if (!prev) return null;
        return sorted.find((u) => u.usuari === prev.usuari) || prev;
      });
    } catch (err) {
      console.error('Error al carregar usuaris:', err);
      const msg = extractErrorMessage(err, 'Error al carregar la llista d’usuaris');
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filtrar usuaris per cerca
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const q = searchTerm.toLowerCase();
      return (
        u.usuari.toLowerCase().includes(q) ||
        (u.email && u.email.toLowerCase().includes(q))
      );
    });
  }, [users, searchTerm]);

  // Obrir Creació d'Usuari
  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({ usuari: '', email: '', actiu: true });
    setOpenUserModal(true);
  };

  // Obrir Edició d'Usuari
  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setFormData({
      usuari: user.usuari,
      email: user.email || '',
      actiu: user.actiu ?? true
    });
    setOpenUserModal(true);
  };

  // Desar Usuari
  const handleSaveUser = async () => {
    const usuariClean = formData.usuari.trim();
    const emailClean = formData.email.trim();

    if (!usuariClean) {
      showNotification('L’identificador d’usuari és obligatori', 'warning');
      return;
    }
    if (!emailClean) {
      showNotification('El correu electrònic de Google és obligatori', 'warning');
      return;
    }

    setSavingUser(true);
    try {
      if (editingUser) {
        // PUT /api/v1/usuari/{usuari}
        await axios.put(
          `${config.apiBaseUrl}/v1/usuari/${encodeURIComponent(editingUser.usuari)}`,
          {
            usuari: editingUser.usuari,
            email: emailClean,
            actiu: formData.actiu
          },
          { headers: { Authorization: jwtToken } }
        );
        showNotification(`Usuari "${editingUser.usuari}" actualitzat correctament`, 'success');
      } else {
        // POST /api/v1/usuari/
        await axios.post(
          `${config.apiBaseUrl}/v1/usuari/`,
          {
            usuari: usuariClean,
            email: emailClean,
            actiu: formData.actiu
          },
          { headers: { Authorization: jwtToken } }
        );
        showNotification(`Usuari "${usuariClean}" creat correctament`, 'success');
      }
      setOpenUserModal(false);
      fetchUsers();
      if (onReloadAll) onReloadAll();
    } catch (err) {
      console.error('Error al desar usuari:', err);
      const msg = extractErrorMessage(err, 'Error en desar l’usuari');
      showNotification(msg, 'error');
    } finally {
      setSavingUser(false);
    }
  };

  // Clic en canviar estat usuari (Actiu / Inactiu)
  const handleRequestToggle = (user) => {
    if (isProtectedAdmin(user) && user.actiu) {
      showNotification(
        "No es pot desactivar l'usuari administrador principal (cafarre@gmail.com).",
        'warning'
      );
      return;
    }

    if (user.actiu) {
      setToggleTarget(user);
      setOpenToggleDialog(true);
    } else {
      executeToggle(user);
    }
  };

  // Executar Toggle
  const executeToggle = async (target) => {
    const user = target || toggleTarget;
    if (!user) return;
    setToggling(true);
    try {
      if (user.actiu) {
        // Desactivació: intenta PATCH /toggle-actiu, si falla per CORS preflight o 405, fallback a DELETE /{id}
        try {
          await axios.patch(
            `${config.apiBaseUrl}/v1/usuari/${encodeURIComponent(user.usuari)}/toggle-actiu`,
            {},
            { headers: { Authorization: jwtToken } }
          );
        } catch (patchErr) {
          if (!patchErr.response || patchErr.response.status === 405) {
            await axios.delete(
              `${config.apiBaseUrl}/v1/usuari/${encodeURIComponent(user.usuari)}`,
              { headers: { Authorization: jwtToken } }
            );
          } else {
            throw patchErr;
          }
        }
      } else {
        // Activació: intenta PATCH /toggle-actiu, si falla per CORS preflight o 405, fallback a PUT /{id}
        try {
          await axios.patch(
            `${config.apiBaseUrl}/v1/usuari/${encodeURIComponent(user.usuari)}/toggle-actiu`,
            {},
            { headers: { Authorization: jwtToken } }
          );
        } catch (patchErr) {
          if (!patchErr.response || patchErr.response.status === 405) {
            await axios.put(
              `${config.apiBaseUrl}/v1/usuari/${encodeURIComponent(user.usuari)}`,
              { usuari: user.usuari, email: user.email, actiu: true },
              { headers: { Authorization: jwtToken } }
            );
          } else {
            throw patchErr;
          }
        }
      }

      showNotification(
        `Estat de l’usuari "${user.usuari}" canviat a ${!user.actiu ? 'Actiu' : 'Inactiu'}`,
        'success'
      );
      setOpenToggleDialog(false);
      fetchUsers();
      if (onReloadAll) onReloadAll();
    } catch (err) {
      console.error('Error al commutar estat d’usuari:', err);
      const msg = extractErrorMessage(err, 'Error al canviar l’estat de l’usuari');
      showNotification(msg, 'error');
      setOpenToggleDialog(false);
    } finally {
      setToggling(false);
    }
  };

  // Obrir modal de comptes de l'usuari
  const handleOpenComptes = (user) => {
    setSelectedUserForComptes(user);
    setNewCompteApp('');
    const maxOrdre = (user.comptes || []).reduce((acc, c) => Math.max(acc, c.ordre || 0), 0);
    setNewCompteOrdre(maxOrdre + 1);
    setOpenComptesModal(true);
  };

  // Afegir compte a l'usuari
  const handleAddCompteToUser = async () => {
    if (!newCompteApp.trim()) {
      showNotification('Introdueix el codi de compte (ex: CFG)', 'warning');
      return;
    }
    setSavingCompte(true);
    try {
      await axios.post(
        `${config.apiBaseUrl}/v1/usuariscomptes/`,
        {
          usuari: selectedUserForComptes.usuari,
          userapp: newCompteApp.trim().toUpperCase(),
          ordre: Number(newCompteOrdre) || 1,
          actiu: true
        },
        { headers: { Authorization: jwtToken } }
      );
      showNotification(`Compte ${newCompteApp.toUpperCase()} assignat a l’usuari`, 'success');
      setNewCompteApp('');
      fetchUsers();
      if (onReloadAll) onReloadAll();
    } catch (err) {
      console.error('Error al assignar compte:', err);
      const msg = extractErrorMessage(err, 'Error al assignar el compte');
      showNotification(msg, 'error');
    } finally {
      setSavingCompte(false);
    }
  };

  // Toggle compte actiu per a l'usuari
  const handleToggleCompte = async (compteItem) => {
    try {
      await axios.patch(
        `${config.apiBaseUrl}/v1/usuariscomptes/${compteItem.id}/toggle-actiu`,
        {},
        { headers: { Authorization: jwtToken } }
      );
      showNotification(`Estat del compte ${compteItem.userapp} actualitzat`, 'success');
      fetchUsers();
      if (onReloadAll) onReloadAll();
    } catch (err) {
      console.error('Error al commutar estat del compte:', err);
      const msg = extractErrorMessage(err, 'Error al canviar l’estat del compte');
      showNotification(msg, 'error');
    }
  };

  // Eliminar assignació de compte
  const handleDeleteCompte = async (compteItem) => {
    try {
      await axios.delete(`${config.apiBaseUrl}/v1/usuariscomptes/${compteItem.id}`, {
        headers: { Authorization: jwtToken }
      });
      showNotification(`Compte ${compteItem.userapp} desvinculat`, 'success');
      fetchUsers();
      if (onReloadAll) onReloadAll();
    } catch (err) {
      console.error('Error al suprimir assignació:', err);
      const msg = extractErrorMessage(err, 'Error al desvincular el compte');
      showNotification(msg, 'error');
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Filtres i Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Cercar per identificador d'usuari o correu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={6} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              fullWidth={isMobile}
              sx={{ textTransform: 'none', fontWeight: 'bold' }}
            >
              Nou Usuari
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Estat de càrrega */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No s’ha trobat cap usuari amb els criteris especificats.
        </Alert>
      ) : isMobile ? (
        /* VISTA MÒBIL: LLISTA DE TARGETES */
        <Stack spacing={1.5}>
          {filteredUsers.map((u) => {
            const protectedUser = isProtectedAdmin(u);
            return (
              <Card key={u.usuari} variant="outlined" sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: u.actiu ? 'primary.main' : 'grey.500', width: 44, height: 44 }}>
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                          {u.usuari}
                        </Typography>
                        {protectedUser && (
                          <Chip label="Admin Principal" size="small" color="secondary" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {u.email}
                      </Typography>
                    </Box>
                    <Chip
                      label={u.actiu ? 'Actiu' : 'Inactiu'}
                      color={u.actiu ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                    />
                  </Box>

                  <Typography variant="caption" color="text.secondary" display="block">
                    <strong>Darrera connexió:</strong> {formatDateTime(u.lastLogin)}
                  </Typography>

                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      <strong>Comptes autoritzats:</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(!u.comptes || u.comptes.length === 0) ? (
                        <Typography variant="caption" color="text.secondary">Cap compte assignat</Typography>
                      ) : (
                        u.comptes.map((c) => (
                          <Chip
                            key={c.id}
                            label={`${c.userapp} (#${c.ordre})`}
                            size="small"
                            color={c.actiu ? 'primary' : 'default'}
                            variant={c.actiu ? 'filled' : 'outlined'}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))
                      )}
                    </Box>
                  </Box>
                </CardContent>
                <Divider />
                <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 0.5 }}>
                  <Tooltip
                    title={
                      protectedUser
                        ? "L'usuari administrador principal no es pot desactivar."
                        : u.actiu
                        ? 'Desactivar accés'
                        : 'Activar accés'
                    }
                  >
                    <span>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(u.actiu)}
                            disabled={protectedUser}
                            onChange={() => handleRequestToggle(u)}
                            color="success"
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            {u.actiu ? 'Activat' : 'Desactivat'}
                          </Typography>
                        }
                      />
                    </span>
                  </Tooltip>
                  <Box>
                    <Button
                      size="small"
                      startIcon={<AccountBalanceIcon />}
                      onClick={() => handleOpenComptes(u)}
                      sx={{ textTransform: 'none', mr: 1 }}
                    >
                      Comptes
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenEdit(u)}
                      sx={{ textTransform: 'none' }}
                    >
                      Editar
                    </Button>
                  </Box>
                </CardActions>
              </Card>
            );
          })}
        </Stack>
      ) : (
        /* VISTA ESCRIPTORI: TAULA COMPLETA */
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Identificador</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Correu Google</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Darrera Connexió</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estat</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Comptes Assignats</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Accions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((u) => {
                const protectedUser = isProtectedAdmin(u);
                return (
                  <TableRow key={u.usuari} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: u.actiu ? 'primary.main' : 'grey.400', width: 28, height: 28, fontSize: '0.85rem' }}>
                          {u.usuari.charAt(0).toUpperCase()}
                        </Avatar>
                        {u.usuari}
                        {protectedUser && (
                          <Chip label="Admin" size="small" color="secondary" sx={{ fontSize: '0.65rem', height: 18 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{formatDateTime(u.lastLogin)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={u.actiu ? 'Actiu' : 'Inactiu'}
                        color={u.actiu ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(!u.comptes || u.comptes.length === 0) ? (
                          <Typography variant="caption" color="text.secondary">Sense comptes</Typography>
                        ) : (
                          u.comptes.map((c) => (
                            <Chip
                              key={c.id}
                              label={`${c.userapp} (#${c.ordre})`}
                              size="small"
                              color={c.actiu ? 'primary' : 'default'}
                              variant={c.actiu ? 'filled' : 'outlined'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Gestionar comptes assignats">
                        <IconButton size="small" color="secondary" onClick={() => handleOpenComptes(u)}>
                          <AccountBalanceIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar usuari">
                        <IconButton size="small" color="primary" onClick={() => handleOpenEdit(u)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          protectedUser
                            ? "L'usuari administrador principal no es pot desactivar."
                            : u.actiu
                            ? 'Desactivar accés'
                            : 'Activar accés'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color={u.actiu ? 'default' : 'success'}
                            disabled={protectedUser}
                            onClick={() => handleRequestToggle(u)}
                          >
                            <Switch size="small" checked={Boolean(u.actiu)} disabled={protectedUser} pointerEvents="none" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* DIÀLEG: CREAR / EDITAR USUARI */}
      <Dialog
        open={openUserModal}
        onClose={() => !savingUser && setOpenUserModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          {editingUser ? `Editar Usuari: ${editingUser.usuari}` : 'Nou Usuari del Sistema'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Alert severity="info">
              El correu electrònic ha de ser el mateix compte de Google amb què l’usuari iniciarà sessió.
            </Alert>

            <TextField
              label="Identificador d’Usuari"
              fullWidth
              required
              disabled={Boolean(editingUser)}
              value={formData.usuari}
              onChange={(e) => setFormData({ ...formData, usuari: e.target.value.toLowerCase().trim() })}
              placeholder="Ex: maria, jordi, admin..."
              helperText={
                editingUser
                  ? 'L’identificador és la clau primària i no es pot alterar.'
                  : 'Identificador únic del perfil.'
              }
            />

            <TextField
              label="Correu Electrònic Google"
              fullWidth
              required
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="usuari@gmail.com"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.actiu}
                  onChange={(e) => setFormData({ ...formData, actiu: e.target.checked })}
                  color="success"
                  disabled={editingUser && isProtectedAdmin(editingUser)}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Usuari Actiu (Té permís d'accés a l'aplicació)
                </Typography>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenUserModal(false)} disabled={savingUser} color="inherit">
            Cancel·lar
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            color="primary"
            disabled={savingUser}
          >
            {savingUser ? <CircularProgress size={24} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÀLEG: CONFIRMACIÓ DESACTIVACIÓ D'USUARI */}
      <Dialog
        open={openToggleDialog}
        onClose={() => !toggling && setOpenToggleDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Desactivar Usuari
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            En desactivar l’usuari, el backend denegarà immediatament qualsevol intent d’inici de sessió (error 401 Unauthorized), però es mantindrà tot el registre històric i d’auditoria.
          </Alert>
          <Typography variant="body2">
            Vols confirmar la desactivació de <strong>"{toggleTarget?.usuari}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenToggleDialog(false)} disabled={toggling} color="inherit">
            Cancel·lar
          </Button>
          <Button
            onClick={() => executeToggle()}
            variant="contained"
            color="warning"
            disabled={toggling}
          >
            {toggling ? <CircularProgress size={24} /> : 'Desactivar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÀLEG: GESTIÓ DE COMPTES DE L'USUARI */}
      <Dialog
        open={openComptesModal}
        onClose={() => setOpenComptesModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceIcon color="primary" />
          Comptes Assignats a: {selectedUserForComptes?.usuari}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Assignar Nou Compte:
          </Typography>
          <Grid container spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Codi de Compte (UserApp)"
                fullWidth
                size="small"
                value={newCompteApp}
                onChange={(e) => setNewCompteApp(e.target.value)}
                placeholder="Ex: CFG, ESTALVI..."
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Ordre"
                type="number"
                fullWidth
                size="small"
                value={newCompteOrdre}
                onChange={(e) => setNewCompteOrdre(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleAddCompteToUser}
                disabled={savingCompte || !newCompteApp.trim()}
                sx={{ textTransform: 'none' }}
              >
                Assignar
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Comptes Actuals:
          </Typography>

          {(!selectedUserForComptes?.comptes || selectedUserForComptes.comptes.length === 0) ? (
            <Alert severity="info">Aquest usuari no té cap compte associat encara.</Alert>
          ) : (
            <List dense disablePadding>
              {selectedUserForComptes.comptes.map((c) => (
                <ListItem
                  key={c.id}
                  sx={{
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    mb: 1,
                    pr: 12
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {c.userapp}
                        </Typography>
                        <Chip
                          label={`Ordre: ${c.ordre}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </Box>
                    }
                    secondary={c.actiu ? 'Activat per a aquest usuari' : 'Desactivat'}
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(c.actiu)}
                          onChange={() => handleToggleCompte(c)}
                          color="success"
                        />
                      }
                      label=""
                    />
                    <IconButton
                      edge="end"
                      size="small"
                      color="error"
                      onClick={() => handleDeleteCompte(c)}
                      title="Eliminar assignació"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenComptesModal(false)} variant="contained">
            Tancar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsuarisAdmin;
